import { useCallback, useMemo, useState } from 'react';

import useSWR from 'swr';

import axios from '../../../axios/Utils/interceptors';
import { GET_SINIESTRO_IMAGENES } from '../../../axios/Utils/constants';
import { useDocumentacion as useDocumentacionContext } from '../../../context/DocumentacionContext';
import { useSiniestros } from '../../../context/SiniestrosContext';
import { DOCUMENT_HANDLERS } from '../../../utils/documentacion';
import { normalizeS3FileUrl } from '../../../utils/s3';
import { useAlert } from '../../UI/useAlert';
import useCameraFilePicker from '../../Utils/useCameraFilePicker';
import { useDocumentacion } from './useDocumentacion';

type CBUType = 'propio' | 'tercero';

type FileInputConfig = {
  key: string;
  fetch: string;
  label: string;
};

type FileUploaderOptions = {
  fileIndex?: number;
  label?: string;
  mode?: 'upload' | 'view';
  emptyUpload?: boolean;
};

const FILE_INPUTS: FileInputConfig[] = [
  { key: 'dni_frente', fetch: 'dniFrente', label: 'DNI frente' },
  { key: 'dni_dorso', fetch: 'dniDorso', label: 'DNI dorso' },
  { key: 'libreta_vacunacion', fetch: 'libretas', label: 'Libreta de vacunación' },
  { key: 'cbu', fetch: 'cbu', label: 'CBU' },
  { key: 'tercero_cbu', fetch: 'cbuTercero', label: 'CBU de tercero' },
  { key: 'constancia', fetch: 'constancia', label: 'Constancia de tercero' },
];

/* =========================
 * SWR FETCHER (AXIOS)
 * ========================= */
const fetcher = async (url: string) => {
  const res = await axios.get(url);
  return res.data;
};

const useDocumentacionHandler = () => {
  /* =========================
   * UI STATE
   * ========================= */
  const [uiState, setUiState] = useState({
    showCameraOptions: false,
    showGallery: false,
    cbuType: 'propio' as CBUType,
  });

  const [currentElement, setCurrentElement] = useState<FileInputConfig | null>(null);
  const [galleryUrl, setGalleryUrl] = useState<string>();
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  /* =========================
   * CONTEXT / HOOKS
   * ========================= */
  const { currentSiniestro } = useSiniestros();
  const { mutateDocumentacion } = useDocumentacionContext();
  const { postDocumento } = useDocumentacion();
  const { alertState, showAlert, hideAlert } = useAlert();

  const siniestroId = currentSiniestro?.id;

  /* =========================
   * SWR
   * ========================= */
  const swrKey = siniestroId
    ? `${GET_SINIESTRO_IMAGENES}?siniestroId=${encodeURIComponent(String(siniestroId))}`
    : null;

  const { data, error, isValidating, mutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 300000, // 5 min — evita re-fetch al reabrir el modal; mutate() post-upload lo ignora
  });

  /* =========================
   * HELPERS
   * ========================= */
  const setLoading = (key: string, value: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: value }));
  };

  const updateUiState = useCallback(
    (updates: Partial<typeof uiState>) => {
      setUiState((prev) => ({ ...prev, ...updates }));
    },
    [uiState],
  );

  /* =========================
   * CAMERA / FILE PICKER
   * ========================= */
  const cameraPicker = useCameraFilePicker({
    onFileSelected: async (file) => {
      if (!currentElement || !siniestroId) return;

      setLoading(currentElement.key, true);
      try {
        await postDocumento(
          siniestroId,
          currentElement.key as keyof typeof DOCUMENT_HANDLERS,
          file,
          mutate,
        );
        await mutateDocumentacion();
        showAlert({
          header: 'Carga exitosa',
          message: `${currentElement.label} subido exitosamente`,
          buttons: [{ text: 'Entendido' }],
        });
      } catch (error) {
        console.error('Error al subir documento:', error);
      } finally {
        setLoading(currentElement.key, false);
      }
    },
  });

  /* =========================
   * GALERÍA
   * ========================= */
  const openGalleryWithFile = useCallback(
    (fetchKey: string, fileIndex?: number) => {
      const rawFiles = data?.[fetchKey];
      const files = Array.isArray(rawFiles)
        ? rawFiles
        : rawFiles
          ? [String(rawFiles)]
          : [];
      if (!files.length) return;

      const targetIndex = fileIndex ?? files.length - 1;
      const fileUrl = files[targetIndex];
      if (!fileUrl) return;

      setGalleryUrl(fileUrl);
      updateUiState({ showGallery: true });
    },
    [data, updateUiState],
  );

  const getUploadedFiles = useCallback(
    (element: FileInputConfig): string[] => {
      const uploadedFiles = data?.[element.fetch];
      if (Array.isArray(uploadedFiles)) return uploadedFiles;
      if (uploadedFiles) return [String(uploadedFiles)];
      return [];
    },
    [data],
  );

  /* =========================
   * FILE UPLOADER PROPS
   * ========================= */
  const getFileUploaderProps = useCallback(
    (element: FileInputConfig, options: FileUploaderOptions = {}) => {
      const normalizedUploadedFiles = getUploadedFiles(element);
      const selectedUploadedFile =
        typeof options.fileIndex === 'number'
          ? normalizedUploadedFiles[options.fileIndex]
          : undefined;
      const isViewMode = options.mode === 'view';
      const visibleUploadedFiles = isViewMode
        ? selectedUploadedFile
          ? [selectedUploadedFile]
          : undefined
        : options.emptyUpload
          ? undefined
          : normalizedUploadedFiles.length
            ? normalizedUploadedFiles
            : undefined;
      const counter = isViewMode
        ? selectedUploadedFile
          ? 1
          : 0
        : options.emptyUpload
          ? 0
          : normalizedUploadedFiles.length;
      const canViewFile = isViewMode
        ? Boolean(selectedUploadedFile)
        : !options.emptyUpload && normalizedUploadedFiles.length > 0;

      return {
        fileData: {
          label: options.label ?? element.label,
          isLoading:
            isViewMode || options.emptyUpload
              ? false
              : loadingStates[element.key] || false,
          uploaded: visibleUploadedFiles,
          disabled: false,
          counter,
          canViewFile,
          showUploadAction: !isViewMode,
          showUploadedState: options.emptyUpload ? false : undefined,
        },
        onShowCameraOptions: () => {
          setCurrentElement(element);
          updateUiState({ showCameraOptions: true });
          cameraPicker.openCameraOptions();
        },
        inputRefSetter: (el: HTMLInputElement | null) => {
          cameraPicker.fileInputRef.current = el;
        },
        onFileChange: cameraPicker.handlePDFChange,
        onViewFile: () => openGalleryWithFile(element.fetch, options.fileIndex),
      };
    },
    [loadingStates, cameraPicker, openGalleryWithFile, getUploadedFiles, updateUiState],
  );

  /* =========================
   * FILTROS
   * ========================= */
  const filteredInputs = useMemo(
    () => ({
      basic: FILE_INPUTS.slice(0, 3),
      cbu: FILE_INPUTS.filter((el) => el.key === 'cbu'),
      tercero: FILE_INPUTS.filter((el) => ['tercero_cbu', 'constancia'].includes(el.key)),
    }),
    [],
  );

  /* =========================
   * API PÚBLICA
   * ========================= */
  return {
    // data
    documentacion: data,
    error,
    isValidating,

    // ui
    cbuType: uiState.cbuType,
    showCameraOptions: uiState.showCameraOptions,
    showGallery: uiState.showGallery,
    galleryUrl,
    uploadAlertState: alertState,

    setCBU_Type: (type: CBUType) => updateUiState({ cbuType: type }),

    // handlers
    handlePhoto: cameraPicker.handleCamera,
    handleGallery: cameraPicker.handleGallery,
    handlePDFClick: cameraPicker.handlePDFClick,

    closeCameraOptions: () => {
      updateUiState({ showCameraOptions: false });
      cameraPicker.closeCameraOptions();
    },

    closeGallery: () => updateUiState({ showGallery: false }),
    hideUploadAlert: hideAlert,

    // utils
    parseURL: normalizeS3FileUrl,
    getFileUploaderProps,
    getUploadedFiles,

    // inputs
    ...filteredInputs,
  };
};

export default useDocumentacionHandler;
